// Map our internal fuel/transmission/condition values to Schema.org enums.
// Reference: https://schema.org/Car  /  https://schema.org/Vehicle

const FUEL_MAP = {
  benzin: "https://schema.org/PetrolCar",
  diesel: "https://schema.org/DieselFuel",
  elektro: "https://schema.org/ElectricCar",
  hybrid: "https://schema.org/HybridCar",
};

const TRANS_MAP = {
  manuell: "https://schema.org/ManualTransmission",
  automat: "https://schema.org/AutomaticTransmission",
};

const CONDITION_MAP = {
  neu: "https://schema.org/NewCondition",
  occasion: "https://schema.org/UsedCondition",
  vorfuhr: "https://schema.org/UsedCondition",
  oldtimer: "https://schema.org/UsedCondition",
};

export function buildVehicleSchema(car, { canonicalUrl, fileBaseUrl, sellerType } = {}) {
  if (!car) return null;
  const images = (car.images || []).map((p) => `${fileBaseUrl}/api/files/${p}`);
  const status = car.status === "active"
    ? "https://schema.org/InStock"
    : "https://schema.org/SoldOut";

  // Build full PostalAddress when we have a location string (Swiss canton/city).
  const sellerAddress = car.location ? {
    "@type": "PostalAddress",
    addressLocality: car.location,
    addressCountry: "CH",
  } : undefined;

  // Phase 69 — Surface seller-confirmed condition flags as additionalProperty
  // so Google's product / vehicle rich-results pick them up. Each entry is a
  // PropertyValue per Schema.org guidance.
  const additionalProperty = [];
  if (car.accident_free === true)      additionalProperty.push({ "@type": "PropertyValue", name: "Accident-free", value: true });
  if (car.non_smoker === true)         additionalProperty.push({ "@type": "PropertyValue", name: "Non-smoker vehicle", value: true });
  if (car.service_book === true)       additionalProperty.push({ "@type": "PropertyValue", name: "Full service history", value: true });
  if (car.warranty_available === true) additionalProperty.push({ "@type": "PropertyValue", name: "Warranty available", value: true });
  if (car.ab_mfk)                       additionalProperty.push({ "@type": "PropertyValue", name: "Fresh roadworthy certificate (MFK)", value: true });
  if (car.new_tires)                    additionalProperty.push({ "@type": "PropertyValue", name: "New tyres", value: true });
  if (car.mfk_valid_until)              additionalProperty.push({ "@type": "PropertyValue", name: "Last roadworthy check", value: car.mfk_valid_until });
  if (car.last_service_date)            additionalProperty.push({ "@type": "PropertyValue", name: "Last service date", value: car.last_service_date });
  if (Number(car.number_of_keys) > 0)   additionalProperty.push({ "@type": "PropertyValue", name: "Number of keys", value: Number(car.number_of_keys) });
  if (Number(car.battery_health_pct) > 0) additionalProperty.push({ "@type": "PropertyValue", name: "Battery state of health", value: Number(car.battery_health_pct), unitText: "%" });

  return {
    "@context": "https://schema.org",
    "@type": "Car",
    "@id": canonicalUrl,
    name: car.title,
    description: (car.description || `${car.brand} ${car.model} (${car.year})`).slice(0, 5000),
    brand: { "@type": "Brand", name: car.brand },
    manufacturer: { "@type": "Organization", name: car.brand },
    model: car.model,
    vehicleModelDate: String(car.year),
    productionDate: String(car.year),
    bodyType: car.body_type || undefined,
    color: car.color || undefined,
    fuelType: FUEL_MAP[car.fuel] || car.fuel,
    vehicleTransmission: TRANS_MAP[car.transmission] || car.transmission,
    driveWheelConfiguration: car.drivetrain || undefined,
    numberOfDoors: car.doors ? Number(car.doors) : undefined,
    seatingCapacity: car.seats ? Number(car.seats) : undefined,
    vehicleInteriorColor: car.interior_color || undefined,
    emissionsCO2: car.co2_g_km ? Number(car.co2_g_km) : undefined,
    fuelEfficiency: car.consumption_l_100km ? {
      "@type": "QuantitativeValue",
      value: car.consumption_l_100km,
      unitText: "l/100km",
    } : undefined,
    meetsEmissionStandard: car.euro_norm || undefined,
    vehicleIdentificationNumber: car.vin || undefined,
    itemCondition: CONDITION_MAP[car.condition] || "https://schema.org/UsedCondition",
    mileageFromOdometer: car.mileage_km != null ? {
      "@type": "QuantitativeValue",
      value: car.mileage_km,
      unitCode: "KMT",
    } : undefined,
    vehicleEngine: (car.power_hp || car.displacement_cc || car.cylinders) ? {
      "@type": "EngineSpecification",
      ...(car.power_hp ? { enginePower: { "@type": "QuantitativeValue", value: car.power_hp, unitCode: "BHP" } } : {}),
      ...(car.displacement_cc ? { engineDisplacement: { "@type": "QuantitativeValue", value: car.displacement_cc, unitCode: "CMQ" } } : {}),
      ...(car.cylinders ? { cylinder: Number(car.cylinders) } : {}),
      ...(car.fuel ? { fuelType: FUEL_MAP[car.fuel] || car.fuel } : {}),
    } : undefined,
    image: images.length ? images : undefined,
    url: canonicalUrl,
    ...(additionalProperty.length ? { additionalProperty } : {}),
    offers: {
      "@type": "Offer",
      price: Math.round(car.price_chf || 0),
      priceCurrency: "CHF",
      availability: status,
      url: canonicalUrl,
      itemCondition: CONDITION_MAP[car.condition] || "https://schema.org/UsedCondition",
      priceValidUntil: car.created_at ? _addOneYear(car.created_at) : undefined,
      seller: {
        "@type": (sellerType || car.seller_type) === "dealer" ? "AutoDealer" : "Person",
        name: car.owner_name || "CarHive Seller",
        ...(sellerAddress ? { address: sellerAddress } : {}),
      },
      ...(car.warranty_months ? {
        warranty: {
          "@type": "WarrantyPromise",
          durationOfWarranty: { "@type": "QuantitativeValue", value: car.warranty_months, unitCode: "MON" },
        },
      } : {}),
    },
  };
}

/** Add 1 year to an ISO date (used for priceValidUntil per Google's recommendation). */
function _addOneYear(iso) {
  try {
    const d = new Date(iso);
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
}

// Locale-aware breadcrumb labels for listing detail.
const BREADCRUMB_LABELS = {
  de: { home: "CarHive", listings: "Fahrzeuge" },
  fr: { home: "CarHive", listings: "Véhicules" },
  it: { home: "CarHive", listings: "Veicoli" },
  en: { home: "CarHive", listings: "Vehicles" },
};

export function buildBreadcrumbSchema({ siteUrl, locale, car }) {
  const lbl = BREADCRUMB_LABELS[locale] || BREADCRUMB_LABELS.de;
  const items = [
    { "@type": "ListItem", position: 1, name: lbl.home, item: `${siteUrl}/${locale}` },
    { "@type": "ListItem", position: 2, name: lbl.listings, item: `${siteUrl}/${locale}/listings` },
    { "@type": "ListItem", position: 3, name: car.title, item: `${siteUrl}/${locale}/listings/${car.id}` },
  ];
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
}

/**
 * Locale-native SEO-page path segment for Brand+Model URLs.
 * Mirrors `/app/frontend/src/lib/seoSlug.js::SEO_BRAND_PATH_BY_LOCALE`.
 */
const SEO_SEG_BY_LOCALE = { de: "autos", fr: "voitures", it: "auto", en: "cars" };

/**
 * Build a BreadcrumbList for an SEO brand or brand/model landing page.
 * Phase 55.2 — Google requires explicit position + item URLs for rich crumbs.
 */
export function buildSeoBrandBreadcrumb({ siteUrl, locale, brand, brandSlug, model, modelSlug, allBrandsLabel }) {
  const lbl = BREADCRUMB_LABELS[locale] || BREADCRUMB_LABELS.de;
  const seg = SEO_SEG_BY_LOCALE[locale] || "autos";
  const items = [
    { "@type": "ListItem", position: 1, name: lbl.home, item: `${siteUrl}/${locale}` },
    { "@type": "ListItem", position: 2, name: allBrandsLabel || "Brands", item: `${siteUrl}/${locale}/${seg}` },
    { "@type": "ListItem", position: 3, name: brand, item: `${siteUrl}/${locale}/${seg}/${brandSlug}` },
  ];
  if (modelSlug && model) {
    items.push({ "@type": "ListItem", position: 4, name: `${brand} ${model}`, item: `${siteUrl}/${locale}/${seg}/${brandSlug}/${modelSlug}` });
  }
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
}

/**
 * Build a rich ItemList where each entry is a typed Vehicle/Car with offer +
 * image. Lets Google understand brand/model SEO pages as a real inventory list
 * (eligible for rich result carousels per https://developers.google.com/search/docs/appearance/structured-data/carousel).
 */
export function buildSeoListingsItemList({ siteUrl, locale, listings, fileBaseUrl, listName }) {
  if (!Array.isArray(listings) || listings.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: listName,
    numberOfItems: listings.length,
    itemListElement: listings.map((it, idx) => {
      const url = `${siteUrl}/${locale}/listings/${it.id}`;
      const img = (it.images && it.images.length) ? `${fileBaseUrl}/api/files/${it.images[0]}` : undefined;
      return {
        "@type": "ListItem",
        position: idx + 1,
        url,
        item: {
          "@type": "Car",
          "@id": url,
          name: it.title,
          url,
          ...(img ? { image: img } : {}),
          brand: { "@type": "Brand", name: it.brand },
          model: it.model,
          ...(it.year ? { vehicleModelDate: String(it.year) } : {}),
          ...(it.mileage_km != null ? {
            mileageFromOdometer: { "@type": "QuantitativeValue", value: it.mileage_km, unitCode: "KMT" },
          } : {}),
          ...(it.fuel ? { fuelType: FUEL_MAP[it.fuel] || it.fuel } : {}),
          ...(it.transmission ? { vehicleTransmission: TRANS_MAP[it.transmission] || it.transmission } : {}),
          offers: {
            "@type": "Offer",
            price: Math.round(it.price_chf || 0),
            priceCurrency: "CHF",
            availability: "https://schema.org/InStock",
            url,
            itemCondition: "https://schema.org/UsedCondition",
          },
        },
      };
    }),
  };
}

export function buildOrganizationSchema(siteUrl) {
  // Phase 71 — surface real CarHive emails / addressCountry for Organization
  // rich-result. Address / phone are intentionally omitted until registered.
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "CarHive",
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    address: {
      "@type": "PostalAddress",
      addressCountry: "CH",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@carhive.ch",
        availableLanguage: ["de", "fr", "it", "en"],
        areaServed: "CH",
      },
      {
        "@type": "ContactPoint",
        contactType: "privacy",
        email: "datenschutz@carhive.ch",
        availableLanguage: ["de", "fr", "it", "en"],
        areaServed: "CH",
      },
      {
        "@type": "ContactPoint",
        contactType: "legal",
        email: "legal@carhive.ch",
        availableLanguage: ["de", "fr", "it", "en"],
        areaServed: "CH",
      },
    ],
    sameAs: [],
  };
}

export function buildWebsiteSchema(siteUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "CarHive",
    url: siteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteUrl}/de/listings?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}
